import React, { useMemo, useState } from 'react';
import StatusBadge from './_internal/StatusBadge.js';
import TierBadge from './_internal/TierBadge.js';
import { defaultT, STATUS_ORDER } from './_internal/constants.js';

/**
 * <TenantList> — Controlled Mandantenliste mit Search/Filter.
 */
export default function TenantList({
  tenants,
  selected,
  onSelect,
  loading = false,
  searchable = true,
  onCreateClick,
  t = defaultT,
}) {
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (tenants || []).filter(tn => {
      if (statusFilter !== 'all' && tn.status !== statusFilter) return false;
      if (!needle) return true;
      const hay = `${tn.name || ''} ${tn.slug || ''}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [tenants, q, statusFilter]);

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      background: 'var(--bg)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg, 10px)', overflow: 'hidden', minWidth: 280,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 14px', borderBottom: '1px solid var(--border)',
        background: 'var(--bg2)',
      }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: 'var(--text3)',
          textTransform: 'uppercase', letterSpacing: '0.6px',
        }}>
          {t('shared.tenant.list_header', 'TENANTS')} ({filtered.length})
        </div>
        {onCreateClick && (
          <button className="btn btn-primary btn-sm" onClick={onCreateClick}>
            {t('shared.tenant.create_btn', '+ New Tenant')}
          </button>
        )}
      </div>

      {searchable && (
        <div style={{
          display: 'flex', gap: 8, padding: '10px 14px',
          borderBottom: '1px solid var(--border)',
        }}>
          <input
            className="field-input"
            type="search"
            placeholder={t('shared.tenant.search_placeholder', 'Search…')}
            value={q}
            onChange={e => setQ(e.target.value)}
            style={{ flex: 1, fontSize: 12 }}
          />
          <select
            className="field-input"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            style={{ fontSize: 12, maxWidth: 130 }}
          >
            <option value="all">{t('shared.tenant.filter_all', 'All')}</option>
            {STATUS_ORDER.map(s => (
              <option key={s} value={s}>
                {t(`shared.tenant.status.${s}`, s)}
              </option>
            ))}
          </select>
        </div>
      )}

      <div style={{ flex: 1, overflowY: 'auto', maxHeight: 480 }}>
        {loading ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
            {t('shared.tenant.loading', 'Loading…')}
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
            {t('shared.tenant.no_tenants', 'No tenants found.')}
          </div>
        ) : (
          filtered.map(tn => {
            const active = selected?.id === tn.id;
            return (
              <div
                key={tn.id}
                role="button"
                tabIndex={0}
                onClick={() => onSelect(tn)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault(); onSelect(tn);
                  }
                }}
                style={{
                  display: 'flex', flexDirection: 'column', gap: 4,
                  padding: '10px 14px', cursor: 'pointer',
                  borderBottom: '1px solid var(--border)',
                  background: active ? 'var(--bg3, #eef2ff)' : 'transparent',
                  borderLeft: active ? '3px solid var(--accent, #6366f1)' : '3px solid transparent',
                  transition: 'background 0.12s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{
                    fontSize: 13, fontWeight: 600, color: 'var(--text)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {tn.name}
                  </div>
                  <StatusBadge status={tn.status} t={t} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 11, color: 'var(--text3)' }}>{tn.slug}</span>
                  {tn.licenses?.tier && <TierBadge tier={tn.licenses.tier} t={t} />}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
